import type { IEditableGameContent } from '@/pages/common/gameContent/EditGameContent';

import { enqueueSnackbar } from 'notistack';
import { Activity, useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { IconPlus, IconTrash } from '@tabler/icons-react';

import { Breadcrumb } from '@/components/BreadCrumbs';
import ConfirmationModal from '@/components/ConfirmationModal';
import CustomModal from '@/components/CustomModal';
import { useFormatText } from '@/hooks/useFormatText';
import { useGameContent } from '@/pages/common/gameContent/hooks/useGameContent';
import {
  removeTeacherClassLesson,
  getTeacherClassLessonDetails,
  getTeacherClassLessonBreakdown,
  removeTeacherLessonBreakdownGame,
  getTeacherClassLessonAvailableGames,
} from '@/service/lessonService';
import {
  IGameDropdownItem,
  ITeacherLessonDetails,
  ITeacherLessonBreakdownItem,
} from '@/types/lesson';

import TeacherLessonDetailsContent from './TeacherLessonDetailsContent';

type TStep = 'details';

const ADD_GAME_LOC_KEY = 'lessons.details-drawer.add-game';

interface ITeacherLessonDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  lessonId: string | null;
  onSuccess?: () => void;
  className?: string;
}

const TeacherLessonDetailsDrawer = ({
  isOpen,
  onClose,
  lessonId,
  onSuccess,
  className,
}: ITeacherLessonDetailsDrawerProps) => {
  const { formatText } = useFormatText();
  const navigate = useNavigate();
  const params = useParams();

  const [step, setStep] = useState<TStep>('details');
  const [isLoading, setIsLoading] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isRemovingLesson, setIsRemovingLesson] = useState(false);
  const [lessonDetails, setLessonDetails] =
    useState<ITeacherLessonDetails | null>(null);
  const [lessonBreakdown, setLessonBreakdown] = useState<
    ITeacherLessonBreakdownItem[]
  >([]);
  const [isBreakdownLoading, setIsBreakdownLoading] = useState(false);

  const CANCEL_ACTION_LOC_KEY = 'common.actions.cancel';

  const fetchAvailableGames = useCallback(async (): Promise<
    IEditableGameContent[]
  > => {
    if (!lessonId) return [];
    const res = await getTeacherClassLessonAvailableGames(lessonId);
    if (res?.data) {
      return (res.data as IGameDropdownItem[]).map((item) => ({
        id: item.id,
        name: item.title,
        description: item.description,
        is_customizable: item.is_customizable,
        isUpdated: false,
        contentPairs: [],
      }));
    }
    return [];
  }, [lessonId]);

  const { resetGameContent } = useGameContent(
    isOpen && step !== 'details',
    fetchAvailableGames
  );

  useEffect(() => {
    if (isOpen && lessonId) {
      setStep('details');
      setLessonDetails(null);
      setLessonBreakdown([]);
      fetchLessonDetails();
      fetchLessonBreakdown();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, lessonId]);

  const fetchLessonDetails = async () => {
    if (!lessonId) return;
    setIsLoading(true);
    try {
      const classId = params.id || '';
      const res = await getTeacherClassLessonDetails(classId, lessonId);
      if (res?.status) {
        setLessonDetails(res.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLessonBreakdown = async () => {
    if (!lessonId) return;
    setIsBreakdownLoading(true);
    try {
      const res = await getTeacherClassLessonBreakdown(lessonId);
      if (res?.status) {
        setLessonBreakdown(res.data ?? []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsBreakdownLoading(false);
    }
  };

  const handleAddGameClick = () => {
    onClose();
    navigate(`/teacher/classes/${params.id}/add-game`, {
      state: { lessonId, className, lessonTitle: lessonDetails?.title },
    });
  };

  const handleDeleteGame = async (gameId: string) => {
    if (!lessonId) return;
    try {
      const res = await removeTeacherLessonBreakdownGame(lessonId, gameId);
      if (res?.status) {
        enqueueSnackbar(res.message, { variant: 'success' });
        setLessonBreakdown((prev) => prev.filter((item) => item.id !== gameId));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleRemoveLesson = async () => {
    if (!lessonId) return;
    setIsRemovingLesson(true);
    try {
      const classId = params.id || '';
      const res = await removeTeacherClassLesson(classId, lessonId);
      if (res?.status) {
        enqueueSnackbar(res.message, { variant: 'success' });
        setIsRemoveModalOpen(false);
        onClose();
        onSuccess?.();
      } else {
        console.error(res?.message || 'Failed to remove lesson');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsRemovingLesson(false);
    }
  };

  const getBreadcrumbPath = () => {
    const path = [
      { label: formatText('lessons.details-drawer.title'), href: 'details' },
      { label: formatText(ADD_GAME_LOC_KEY), href: 'add-game' },
    ];
    return path;
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-brand-default border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    return (
      <TeacherLessonDetailsContent
        lessonDetails={lessonDetails}
        lessonBreakdown={lessonBreakdown}
        isBreakdownLoading={isBreakdownLoading}
        onDeleteGame={handleDeleteGame}
      />
    );
  };

  const headerContent =
    step === 'details' ? (
      <p className="text-neutral-primary font-medium leading-7 text-lg">
        {formatText('lessons.details-drawer.title')}
      </p>
    ) : (
      <Breadcrumb
        path={getBreadcrumbPath()}
        onNavigateRequest={(href) => {
          if (href === 'details') {
            resetGameContent();
            setStep('details');
          }
        }}
      />
    );

  const getCancelButtonProps = () => {
    if (step === 'details') {
      return {
        label: formatText('lessons.actions.remove-lesson'),
        icon: <IconTrash size={16} />,
        className: '!text-red-600 px-2!',
        desructive: true,
        variant: 'link' as const,
        onClick: () => setIsRemoveModalOpen(true),
        disabled: isLoading,
      };
    }

    return {
      label: formatText(CANCEL_ACTION_LOC_KEY),
      onClick: () => {
        resetGameContent();
        setStep('details');
      },
      desructive: true,
      variant: 'link' as const,
      className: 'px-2! text-neutral-primary!',
    };
  };

  const getConfirmButtonProps = () => {
    if (step === 'details') {
      return {
        label: formatText(ADD_GAME_LOC_KEY),
        variant: 'primary' as const,
        icon: <IconPlus size={16} />,
        onClick: handleAddGameClick,
        disabled: isLoading,
      };
    }
  };

  return (
    <>
      <CustomModal
        setIsOpen={(open) => {
          if (!open) onClose();
        }}
        isHeader
        isFooter
        position="right"
        className="max-w-150"
        headercontent={headerContent}
        cancelButtonProps={getCancelButtonProps()}
        confirmButtonProps={getConfirmButtonProps()}
      >
        {renderContent()}
      </CustomModal>

      <Activity mode={isRemoveModalOpen ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isRemoveModalOpen}
          setIsOpen={setIsRemoveModalOpen}
          title={formatText('lessons.actions.remove-lesson-title')}
          descriptionPrefix={formatText('common.confirmation.remove-prefix')}
          descriptionSuffix={formatText('lessons.actions.remove-lesson-suffix')}
          entityName={lessonDetails?.title ?? ''}
          confirmLabel={formatText('lessons.actions.remove-lesson')}
          cancelLabel={formatText('common.actions.cancel')}
          onConfirm={handleRemoveLesson}
          isLoading={isRemovingLesson}
          isDestructive
        />
      </Activity>
    </>
  );
};

export default TeacherLessonDetailsDrawer;
